import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useNavigate } from 'react-router';
import { authApi } from '../api/auth.api';
import { publicPaths } from '@/app/router/paths/publicPaths';
import { useAuthStore } from '@/core/stores/authStore';

export function useLogout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearAuth = useAuthStore((s) => s.logout);

  // Tear down the session locally: drop auth state, wipe the React Query cache
  // (so the next user never sees the previous one's cached data), then redirect.
  const endSession = () => {
    clearAuth();
    queryClient.clear();
    navigate(publicPaths.LOGIN);
  };

  const mutation = useMutation({
    mutationFn: () => authApi.signOut(),
    onSuccess: endSession,
    onError: (error: unknown) => {
      // If backend says token is already invalid (401), clear local state too;
      // otherwise keep the session and let the caller surface the error.
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 401) {
        endSession();
      }
    },
  });

  return {
    logout: mutation.mutateAsync,
    isLoggingOut: mutation.isPending,
  };
}
